import { useState } from 'react';
import { Landmark, TrendingDown, Scale, Download, Loader2 } from 'lucide-react';
import { KpiCard } from '../../societies/components/tabs/dashboard/KpiCard';
import { EvolutionChart } from './EvolutionChart';
import { useProvisionExport } from '@/routes/admin/Vendors/VendorFinancial/useFinancialCompiled';
import type { ProvisionReport } from '../types/dashboard.types';

interface WarrantyOption {
  name: string;
  duration: number;
}

interface ProvisionSectionProps {
  report: ProvisionReport;
  societyId: string;
  filters?: { dateFrom?: string; dateTo?: string; storeId?: string };
  warrantyOptions?: WarrantyOption[];
  selectedWarrantyName?: string;
  onWarrantyChange?: (name: string) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);

export const ProvisionSection: React.FC<ProvisionSectionProps> = ({ report, societyId, filters, warrantyOptions, selectedWarrantyName, onWarrantyChange }) => {
  const [exporting, setExporting] = useState(false);
  const provisionExportQuery = useProvisionExport(
    societyId,
    { ...filters, warrantyName: selectedWarrantyName },
    { enabled: false },
  );

  const exportProvisionCsv = async () => {
    setExporting(true);
    try {
      const { data: rows } = await provisionExportQuery.refetch();
      if (!rows || rows.length === 0) { setExporting(false); return; }

      const escapeCsv = (value: string): string =>
        value.includes(';') || value.includes('"') || value.includes('\n')
          ? '"' + value.replace(/"/g, '""') + '"' : value;
      const formatEuros = (v: unknown): string => (v === null || v === undefined ? '' : Number(v).toFixed(2).replace('.', ','));
      const formatDate = (d: string | Date | null | undefined): string => {
        if (!d) return '';
        return new Date(d).toLocaleDateString('fr-FR');
      };
      const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

      const columns = [
        { header: 'Date de création du contrat', getValue: (r: any) => formatDate(r.contractDateCreation) },
        { header: 'N° contrat', getValue: (r: any) => str(r.contractLabel) },
        { header: 'Montant provisionné (€)', getValue: (r: any) => formatEuros(r.warrantyProvision) },
        { header: 'N° SAV', getValue: (r: any) => str(r.savIds) },
        { header: 'Date du SAV', getValue: (r: any) => str(r.savDates) },
        { header: 'Montant consommé (€)', getValue: (r: any) => formatEuros(r.amountConsumed) },
      ];

      const headerLine = columns.map(col => escapeCsv(col.header)).join(';');
      const dataLines = rows.map((row: any) => columns.map(col => escapeCsv(col.getValue(row))).join(';'));
      const csvContent = '\uFEFF' + [headerLine, ...dataLines].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `suivi-provision-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      {/* Bandeau titre */}
      <div className="tw-flex tw-items-center tw-justify-between tw-bg-affinicar-primary tw-text-white tw-uppercase tw-font-bold tw-px-4 tw-py-4 tw-rounded-lg tw-mb-4 tw-min-h-[56px]">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Landmark className="tw-w-4 sm:tw-w-5 tw-h-4 sm:tw-h-5 tw-shrink-0" />
          <span className="tw-text-sm sm:tw-text-base tw-font-bold tw-leading-none">Suivi de la provision</span>
        </div>
        <button
          onClick={exportProvisionCsv}
          disabled={exporting}
          className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-white/20 tw-rounded-md tw-text-sm tw-font-medium tw-normal-case hover:tw-bg-white/30 tw-transition-colors disabled:tw-opacity-50"
        >
          {exporting ? <Loader2 size={14} className="tw-animate-spin" /> : <Download size={14} />}
          <span className="tw-hidden sm:tw-inline">{exporting ? 'Export...' : 'Export'}</span>
        </button>
      </div>

      {/* Filtre garantie */}
      {warrantyOptions && warrantyOptions.length > 0 && onWarrantyChange && (
        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-gap-2 sm:tw-gap-3 tw-mb-4 tw-mt-2">
          <label className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-uppercase tw-whitespace-nowrap">Garantie</label>
          <select
            value={selectedWarrantyName || ''}
            onChange={(e) => onWarrantyChange(e.target.value)}
            className="tw-w-full sm:tw-w-auto tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm tw-bg-white focus:tw-ring-1 focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary"
          >
            <option value="">Toutes les garanties</option>
            {warrantyOptions.map((w) => (
              <option key={`${w.name}-${w.duration}`} value={w.name}>
                {w.name} ({w.duration} mois)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* KPIs */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-4">
        <KpiCard
          title="Alimentation de la provision"
          value={formatCurrency(report.alimentation)}
          subtitle="Montant alimenté par les provisions de vos garanties"
          icon={Landmark}
          color="affinicar"
        />
        <KpiCard
          title="Consommation de la provision"
          value={formatCurrency(report.consommation)}
          subtitle="Montant impacté par les prises en charge réalisées"
          icon={TrendingDown}
          color="red"
        />
        <KpiCard
          title="Soulte de la provision"
          value={formatCurrency(report.soulte)}
          subtitle="Différence entre alimentation et consommation"
          icon={Scale}
          color={report.soulte >= 0 ? 'green' : 'red'}
        />
      </div>

      {/* Graphique évolution */}
      <div className="tw-grid tw-grid-cols-1 tw-gap-4">
        <EvolutionChart
          title="Évolution de votre provision"
          data={report.monthly}
          series={[
            {
              dataKey: 'soulte',
              name: 'Soulte',
              color: '#059669',
              isCurrency: true,
            },
          ]}
        />
      </div>
    </div>
  );
};
